import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AdminLayout } from "@/components/AdminLayout";
import { api, errText, fmt, API } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Copy, Mail, MessageSquareText, Trash2, Upload, RefreshCw, ExternalLink } from "lucide-react";

const STATUSES = ["Draft", "Sent", "Viewed", "Package Selected", "Invoice Created", "Deposit Paid", "Paid in Full", "Expired", "Closed"];
const SIZES = [
  { v: "auto", l: "Automatic" },
  { v: "small", l: "Small" },
  { v: "medium", l: "Medium" },
  { v: "large", l: "Large" },
  { v: "multi", l: "Multi-Piece" },
];

const Section = ({ title, children }) => (
  <div className="bg-white border border-gray-200 p-6 sm:p-8">
    <h2 className="font-display text-xl mb-6">{title}</h2>
    {children}
  </div>
);

const F = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-[11px] uppercase tracking-[0.14em] text-gray-500">{label}</Label>
    {children}
  </div>
);

export default function QuoteBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;
  const [settings, setSettings] = useState(null);
  const [quote, setQuote] = useState(null);
  const [form, setForm] = useState({
    customer: { first_name: "", last_name: "", email: "", phone: "" },
    project_title: "",
    internal_notes: "",
    assessment: "",
    items: {},
    complexity: { type: "none", value: 0, show_to_customer: false },
    size_override: null,
    recommended_package: "complete",
    pickup_date: "",
    balance_due_date: "",
    expiry_date: "",
    realtor_id: null,
    partner_pricing: false,
  });
  const [realtors, setRealtors] = useState([]);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data));
    api.get("/realtors/options").then((r) => setRealtors(r.data)).catch(() => {});
    if (id) {
      api.get(`/quotes/${id}`).then((r) => {
        const q = r.data;
        setQuote(q);
        setForm({
          customer: q.customer,
          project_title: q.project_title,
          internal_notes: q.internal_notes || "",
          assessment: q.assessment || "",
          items: Object.fromEntries(q.items.map((i) => [i.key, i.qty])),
          complexity: q.complexity,
          size_override: q.size_override,
          recommended_package: q.recommended_package,
          pickup_date: q.pickup_date || "",
          balance_due_date: q.balance_due_date || "",
          expiry_date: q.expiry_date || "",
          realtor_id: q.realtor?.id || null,
          partner_pricing: !!q.partner_pricing,
        });
        setPreview(q.pricing);
      }).catch((e) => toast.error(errText(e)));
    }
  }, [id]);

  const payloadItems = useCallback(
    (items) => Object.entries(items).filter(([, q]) => q > 0).map(([key, qty]) => ({ key, qty: Number(qty) })),
    []
  );

  useEffect(() => {
    const t = setTimeout(() => {
      api.post("/quotes/preview", {
        items: payloadItems(form.items),
        complexity: { ...form.complexity, value: Number(form.complexity.value) || 0 },
        size_override: form.size_override,
        partner_pricing: form.partner_pricing && !!form.realtor_id,
      }).then((r) => setPreview(r.data)).catch(() => {});
    }, 350);
    return () => clearTimeout(t);
  }, [form.items, form.complexity, form.size_override, form.partner_pricing, form.realtor_id, payloadItems]);

  const buildPayload = () => ({
    customer: form.customer,
    project_title: form.project_title,
    internal_notes: form.internal_notes,
    assessment: form.assessment,
    items: payloadItems(form.items),
    complexity: { ...form.complexity, value: Number(form.complexity.value) || 0 },
    size_override: form.size_override,
    recommended_package: form.recommended_package,
    pickup_date: form.pickup_date,
    balance_due_date: form.balance_due_date || null,
    expiry_date: form.expiry_date || null,
    realtor_id: form.realtor_id,
    partner_pricing: form.partner_pricing && !!form.realtor_id,
  });

  const save = async () => {
    if (!form.customer.first_name || !form.customer.email || !form.project_title) {
      toast.error("First name, email and project title are required");
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        const { data } = await api.post("/quotes", buildPayload());
        toast.success(`Quote ${data.quote_number} created`);
        navigate(`/admin/quotes/${data.id}`, { replace: true });
      } else {
        const { data } = await api.put(`/quotes/${id}`, buildPayload());
        setQuote(data);
        setPreview(data.pricing);
        toast.success("Quote saved");
      }
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setSaving(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(quote.public_link);
    api.post(`/quotes/${id}/mark-sent`).then(() => setQuote((q) => ({ ...q, status: q.status === "Draft" ? "Sent" : q.status })));
    toast.success("Quote link copied");
  };

  const emailQuote = async () => {
    try {
      await api.post(`/quotes/${id}/send`);
      setQuote((q) => ({ ...q, status: q.status === "Draft" ? "Sent" : q.status }));
      toast.success("Quote emailed to customer");
    } catch (e) {
      toast.error(errText(e));
      const subject = encodeURIComponent("Your ARK Furniture Revival Options");
      const body = encodeURIComponent(`Hi ${form.customer.first_name},\n\nYour personalized ARK Furniture Revival options are ready: ${quote.public_link}\n\nARK Furniture`);
      window.open(`mailto:${form.customer.email}?subject=${subject}&body=${body}`);
    }
  };

  const textQuote = async () => {
    try {
      const { data } = await api.post(`/quotes/${id}/send-sms`);
      setQuote((q) => ({ ...q, status: q.status === "Draft" ? "Sent" : q.status }));
      toast.success(`Quote texted to ${data.phone}`);
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const setStatus = async (status) => {
    await api.post(`/quotes/${id}/status`, { status });
    setQuote((q) => ({ ...q, status }));
    toast.success(`Status set to ${status}`);
  };

  const refreshPayment = async () => {
    try {
      const { data } = await api.post(`/quotes/${id}/refresh-payment`);
      if (data.has_invoice) {
        setQuote((q) => ({ ...q, status: data.quote_status, invoice: { ...q.invoice, status: data.invoice_status } }));
        toast.success(`Invoice status: ${data.invoice_status}`);
      } else toast.info("No invoice yet");
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const uploadPhotos = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    const fd = new FormData();
    [...files].forEach((f) => fd.append("files", f));
    try {
      const { data } = await api.post(`/quotes/${id}/photos`, fd);
      setQuote((q) => ({ ...q, photos: [...(q.photos || []), ...data.photos] }));
      toast.success("Photos uploaded");
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setUploading(false);
    }
  };

  const deletePhoto = async (photoId) => {
    await api.delete(`/quotes/${id}/photos/${photoId}`);
    setQuote((q) => ({ ...q, photos: q.photos.filter((p) => p.id !== photoId) }));
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setCust = (k, v) => setForm((f) => ({ ...f, customer: { ...f.customer, [k]: v } }));

  if (!settings) return <AdminLayout title="Quote Builder"><p className="text-gray-400">Loading...</p></AdminLayout>;

  return (
    <AdminLayout title={isNew ? "New Quote" : `${quote?.quote_number || ""} — ${form.project_title || "Quote"}`}>
      {!isNew && quote && (
        <div className="bg-white border border-gray-200 p-4 mb-8 flex flex-wrap items-center gap-3">
          <Button data-testid="copy-link-btn" onClick={copyLink} variant="outline" className="rounded-sm h-10 text-xs uppercase tracking-wider">
            <Copy size={14} className="mr-1.5" /> Copy Quote Link
          </Button>
          <Button data-testid="email-quote-btn" onClick={emailQuote} variant="outline" className="rounded-sm h-10 text-xs uppercase tracking-wider">
            <Mail size={14} className="mr-1.5" /> Email Quote
          </Button>
          <Button data-testid="text-quote-btn" onClick={textQuote} variant="outline" className="rounded-sm h-10 text-xs uppercase tracking-wider">
            <MessageSquareText size={14} className="mr-1.5" /> Text Quote
          </Button>
          <Button data-testid="open-customer-view-btn" asChild variant="outline" className="rounded-sm h-10 text-xs uppercase tracking-wider">
            <a href={quote.public_link} target="_blank" rel="noreferrer"><ExternalLink size={14} className="mr-1.5" /> Customer View</a>
          </Button>
          <Button data-testid="refresh-payment-btn" onClick={refreshPayment} variant="outline" className="rounded-sm h-10 text-xs uppercase tracking-wider">
            <RefreshCw size={14} className="mr-1.5" /> Refresh Payment
          </Button>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-[11px] uppercase tracking-widest text-gray-500">Status</span>
            <Select value={quote.status} onValueChange={setStatus}>
              <SelectTrigger data-testid="quote-status-select" className="w-44 h-10 rounded-sm"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          <Section title="Customer Information">
            <div className="grid sm:grid-cols-2 gap-5">
              <F label="First Name"><Input data-testid="customer-first-name" value={form.customer.first_name} onChange={(e) => setCust("first_name", e.target.value)} className="rounded-sm h-11" /></F>
              <F label="Last Name"><Input data-testid="customer-last-name" value={form.customer.last_name} onChange={(e) => setCust("last_name", e.target.value)} className="rounded-sm h-11" /></F>
              <F label="Email"><Input data-testid="customer-email" type="email" value={form.customer.email} onChange={(e) => setCust("email", e.target.value)} className="rounded-sm h-11" /></F>
              <F label="Phone"><Input data-testid="customer-phone" value={form.customer.phone} onChange={(e) => setCust("phone", e.target.value)} className="rounded-sm h-11" /></F>
            </div>
          </Section>

          <Section title="Project Information">
            <div className="space-y-5">
              <F label="Project Title"><Input data-testid="project-title" placeholder="Oak Dining Set Revival" value={form.project_title} onChange={(e) => set("project_title", e.target.value)} className="rounded-sm h-11" /></F>
              <F label="Customer-Facing Assessment"><Textarea data-testid="assessment-input" rows={4} placeholder="Your solid oak dining set appears to be an excellent candidate for resurfacing..." value={form.assessment} onChange={(e) => set("assessment", e.target.value)} className="rounded-sm" /></F>
              <F label="Internal Salesperson Notes"><Textarea data-testid="internal-notes" rows={3} value={form.internal_notes} onChange={(e) => set("internal_notes", e.target.value)} className="rounded-sm" /></F>
            </div>
          </Section>

          <Section title="Furniture Pieces">
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
              {settings.furniture_prices.map((p) => (
                <div key={p.key} className="flex items-center justify-between border-b border-gray-100 py-2">
                  <div>
                    <span className="text-sm font-medium">{p.label}</span>
                    <span className="text-xs text-gray-400 ml-2">{fmt(p.price_cents)}</span>
                  </div>
                  <Input
                    data-testid={`qty-${p.key}`}
                    type="number"
                    min="0"
                    value={form.items[p.key] || ""}
                    placeholder="0"
                    onChange={(e) => set("items", { ...form.items, [p.key]: e.target.value === "" ? 0 : Math.max(0, parseInt(e.target.value) || 0) })}
                    className="w-20 h-9 rounded-sm text-center"
                  />
                </div>
              ))}
            </div>
          </Section>

          <Section title="Complexity Adjustment (Optional)">
            <div className="grid sm:grid-cols-3 gap-5 items-end">
              <F label="Type">
                <Select value={form.complexity.type} onValueChange={(v) => set("complexity", { ...form.complexity, type: v })}>
                  <SelectTrigger data-testid="complexity-type-select" className="rounded-sm h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="percent">Custom Percentage</SelectItem>
                    <SelectItem value="amount">Custom Dollar Amount</SelectItem>
                  </SelectContent>
                </Select>
              </F>
              {form.complexity.type !== "none" && (
                <F label={form.complexity.type === "percent" ? "Percent (%)" : "Amount ($)"}>
                  <Input data-testid="complexity-value" type="number" step="0.01" value={form.complexity.value} onChange={(e) => set("complexity", { ...form.complexity, value: e.target.value })} className="rounded-sm h-11" />
                </F>
              )}
              {form.complexity.type !== "none" && (
                <div className="flex items-center gap-3 pb-2.5">
                  <Switch data-testid="complexity-show-switch" checked={form.complexity.show_to_customer} onCheckedChange={(v) => set("complexity", { ...form.complexity, show_to_customer: v })} />
                  <span className="text-sm text-gray-600">Show as line to customer</span>
                </div>
              )}
            </div>
          </Section>

          <Section title="Private Client Program (Realtor Partner)">
            <div className="grid sm:grid-cols-2 gap-5 items-end">
              <F label="Attributed Realtor Partner">
                <Select
                  value={form.realtor_id || "none"}
                  onValueChange={(v) => setForm((f) => ({ ...f, realtor_id: v === "none" ? null : v, partner_pricing: v === "none" ? false : f.partner_pricing }))}
                >
                  <SelectTrigger data-testid="realtor-select" className="rounded-sm h-11"><SelectValue placeholder="No realtor" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No realtor</SelectItem>
                    {realtors.map((r) => (
                      <SelectItem key={r.id} value={r.id}>{r.display_name}{r.brokerage ? ` — ${r.brokerage}` : ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </F>
              <div className="flex items-center gap-3 pb-2.5">
                <Switch
                  data-testid="partner-pricing-switch"
                  checked={form.partner_pricing}
                  disabled={!form.realtor_id}
                  onCheckedChange={(v) => set("partner_pricing", v)}
                />
                <span className="text-sm text-gray-600">
                  Private client pricing — Signature Revival at the Essential rate
                </span>
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-4 leading-relaxed">
              {form.partner_pricing
                ? "The customer receives the full Signature Revival service and pays the Essential rate. Their quote page shows the realtor's branding and their savings."
                : "Attributing a realtor keeps every lead, quote and sale credited to them in the partner dashboard."}
            </p>
          </Section>

          <Section title="Recommendation & Scheduling">
            <div className="grid sm:grid-cols-2 gap-5">
              <F label="Project Size">
                <Select value={form.size_override || "auto"} onValueChange={(v) => set("size_override", v === "auto" ? null : v)}>
                  <SelectTrigger data-testid="size-override-select" className="rounded-sm h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>{SIZES.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}{s.v === "auto" && preview ? ` (${preview.size_label})` : ""}</SelectItem>)}</SelectContent>
                </Select>
              </F>
              <F label="Recommended Package">
                <Select value={form.recommended_package} onValueChange={(v) => set("recommended_package", v)}>
                  <SelectTrigger data-testid="recommended-package-select" className="rounded-sm h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="essential">Essential Revival</SelectItem>
                    <SelectItem value="complete">Complete Revival</SelectItem>
                    <SelectItem value="signature">Signature Revival</SelectItem>
                  </SelectContent>
                </Select>
              </F>
              <F label="Next Available Pickup Date"><Input data-testid="pickup-date-input" placeholder="September 14, 2026" value={form.pickup_date} onChange={(e) => set("pickup_date", e.target.value)} className="rounded-sm h-11" /></F>
              <F label="Balance Due Date (for 50% deposit)"><Input data-testid="balance-due-date-input" type="date" value={form.balance_due_date} onChange={(e) => set("balance_due_date", e.target.value)} className="rounded-sm h-11" /></F>
              <F label="Quote Expiry Date (optional)"><Input data-testid="expiry-date-input" type="date" value={form.expiry_date} onChange={(e) => set("expiry_date", e.target.value)} className="rounded-sm h-11" /></F>
            </div>
          </Section>

          {!isNew && (
            <Section title="Furniture Photos">
              <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={(e) => uploadPhotos(e.target.files)} />
              <Button data-testid="upload-photos-btn" onClick={() => fileRef.current.click()} disabled={uploading} variant="outline" className="rounded-sm mb-5 h-11 text-xs uppercase tracking-wider">
                <Upload size={14} className="mr-1.5" /> {uploading ? "Uploading..." : "Upload Photos"}
              </Button>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                {(quote?.photos || []).map((p) => (
                  <div key={p.id} className="relative group aspect-square">
                    <img src={`${API}/photos/${p.id}`} alt={p.filename} className="w-full h-full object-cover" />
                    <button
                      data-testid={`delete-photo-${p.id}`}
                      onClick={() => deletePhoto(p.id)}
                      className="absolute top-1 right-1 bg-black/70 text-white p-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))}
              </div>
              {(quote?.photos || []).length === 0 && <p className="text-sm text-gray-400">No photos yet. Photos appear prominently on the customer quote.</p>}
            </Section>
          )}
        </div>

        <div className="space-y-6">
          <div className="bg-black text-white p-6 sm:p-8 sticky top-24">
            <h2 className="font-display text-xl mb-1">Pricing Preview</h2>
            <p className="text-[11px] uppercase tracking-[0.15em] text-[#c19a6b] mb-6" data-testid="size-classification">
              {preview ? `${preview.size_label} · ${preview.pieces} piece${preview.pieces === 1 ? "" : "s"}` : "—"}
            </p>
            {preview?.packages?.partner && (
              <div className="border border-[#c19a6b] p-4 mb-5" data-testid="preview-partner">
                <p className="text-[10px] uppercase tracking-[0.18em] text-[#c19a6b]">Private Client Rate · Signature Service</p>
                <div className="flex justify-between items-baseline mt-2">
                  <span className="text-sm text-white/40 line-through">{fmt(preview.packages.partner.retail_subtotal_cents)}</span>
                  <span className="font-display text-2xl text-[#c19a6b]" data-testid="preview-partner-subtotal">{fmt(preview.packages.partner.subtotal_cents)}</span>
                </div>
                <div className="flex justify-between text-xs text-white/50 mt-1.5">
                  <span>Client saves {fmt(preview.packages.partner.savings_cents)}</span>
                  <span>Total {fmt(preview.packages.partner.total_cents)}</span>
                </div>
              </div>
            )}
            {preview && ["essential", "complete", "signature"].map((pkg) => {
              const p = preview.packages[pkg];
              const rec = form.recommended_package === pkg;
              return (
                <div key={pkg} className={`py-4 border-b border-white/10 ${rec ? "text-[#c19a6b]" : ""}`} data-testid={`preview-${pkg}`}>
                  <div className="flex justify-between items-baseline">
                    <span className="text-sm tracking-wide">{p.label}{rec && " ★"}</span>
                    <span className="font-display text-xl" data-testid={`preview-${pkg}-subtotal`}>{fmt(p.subtotal_cents)}</span>
                  </div>
                  <div className="flex justify-between text-xs text-white/50 mt-1">
                    <span>+ HST {fmt(p.hst_cents)}</span>
                    <span>Total {fmt(p.total_cents)}</span>
                  </div>
                </div>
              );
            })}
            {preview && preview.adjustment_cents !== 0 && (
              <p className="text-xs text-white/50 mt-3">Includes {fmt(preview.adjustment_cents)} complexity adjustment</p>
            )}
            <Button
              data-testid="save-quote-btn"
              onClick={save}
              disabled={saving}
              className="w-full mt-8 h-12 bg-[#c19a6b] hover:bg-[#c19a6b]/85 text-white rounded-sm tracking-[0.15em] uppercase text-xs btn-lift"
            >
              {saving ? "Saving..." : isNew ? "Create Quote" : "Save Quote"}
            </Button>
            {!isNew && quote?.invoice && (
              <div className="mt-6 pt-5 border-t border-white/10 text-sm space-y-1.5" data-testid="invoice-info">
                <p className="text-[11px] uppercase tracking-[0.15em] text-[#c19a6b]">Square Invoice</p>
                <p>{fmt(quote.invoice.total_cents)} · {quote.invoice.payment_option === "full" ? "Pay in Full" : "50% Deposit"}</p>
                <p className="text-white/50 text-xs">Status: {quote.invoice.status}</p>
                {quote.invoice.public_url && (
                  <a href={quote.invoice.public_url} target="_blank" rel="noreferrer" className="text-[#c19a6b] underline text-xs">Open Square invoice</a>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
